(() => {
  const initializedShopTheCategories = new WeakSet();
  const shopTheCategoryTabs = new WeakMap();
  const shopTheCategoryRequests = new WeakMap();
  const shopTheCategoryTabAutoplay = new WeakMap();
  const shopTheCategoryTransitionTimers = new WeakMap();
  const shopTheCategoryPanelHeightFrames = new WeakMap();
  const shopTheCategoryPanelHeightCleanups = new WeakMap();
  const productRailCleanups = new WeakMap();
  const productRequests = new Map();
  const productResponseCache = new Map();
  const productCacheStoragePrefix = 'wob:shop-the-category-products:v1:';
  const productCacheTtlMs = 5 * 60 * 1000;
  const productCardCount = 4;
  const tabAutoplayDuration = 8000;
  const tabSwapOutDuration = 120;
  const tabSwapInDuration = 260;
  const mobileShopTheCategoryQuery = window.matchMedia?.('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  const skeletonMarkup = `
    <div class="shop-the-category-products shop-the-category-products--skeleton" aria-hidden="true">
      ${Array.from({ length: productCardCount }, () => `
        <article class="shop-the-category-product-card shop-the-category-product-card--skeleton">
          <span class="shop-the-category-product-card__media product-card__skeleton-media"></span>
          <span class="shop-the-category-product-card__content">
            <span class="product-card__skeleton-line product-card__skeleton-line--title"></span>
            <span class="product-card__skeleton-line product-card__skeleton-line--price"></span>
          </span>
        </article>
      `).join('')}
    </div>
  `;

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const normalizeEndpoint = (endpoint) => String(endpoint || '').trim().replace(/\/+$/, '');

  const hashCacheKey = (key) => {
    let hash = 2166136261;
    const value = String(key || '');

    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  };

  const getSessionStorage = () => {
    try {
      return window.sessionStorage || null;
    } catch (error) {
      return null;
    }
  };

  const getProductCacheStorageKey = (cacheKey) => `${productCacheStoragePrefix}${hashCacheKey(cacheKey)}`;

  const normalizeProductCacheEntry = (entry) => {
    if (!entry || !Array.isArray(entry.products) || Number(entry.expiresAt) <= Date.now()) return null;

    return entry.products;
  };

  const readCachedProducts = (cacheKey) => {
    const memoryProducts = normalizeProductCacheEntry(productResponseCache.get(cacheKey));

    if (memoryProducts) return memoryProducts;

    productResponseCache.delete(cacheKey);

    const storage = getSessionStorage();

    if (!storage) return null;

    try {
      const storageKey = getProductCacheStorageKey(cacheKey);
      const products = normalizeProductCacheEntry(JSON.parse(storage.getItem(storageKey) || 'null'));

      if (!products) {
        storage.removeItem(storageKey);
        return null;
      }

      productResponseCache.set(cacheKey, {
        expiresAt: Date.now() + productCacheTtlMs,
        products
      });
      return products;
    } catch (error) {
      return null;
    }
  };

  const writeCachedProducts = (cacheKey, products) => {
    if (!cacheKey || !Array.isArray(products)) return;

    const entry = {
      expiresAt: Date.now() + productCacheTtlMs,
      products
    };

    productResponseCache.set(cacheKey, entry);

    try {
      getSessionStorage()?.setItem(getProductCacheStorageKey(cacheKey), JSON.stringify(entry));
    } catch (error) {
      // Storage can be unavailable or full; the in-page request cache still handles this visit.
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';

    return String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);
  };

  const withImageWidth = (url, width) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return '';

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      const separator = normalizedUrl.includes('?') ? '&' : '?';

      return `${normalizedUrl}${separator}width=${width}`;
    }
  };

  const buildSrcset = (url) => {
    return [240, 360, 540, 720]
      .map((width) => `${withImageWidth(url, width)} ${width}w`)
      .join(', ');
  };

  const moneyAmount = (money) => {
    const amount = Number(money?.amount ?? money ?? 0);

    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (amount, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getActiveCollectionHandle = (root) => root.dataset.shopTheCategoryCollectionHandle || '';

  const getProductUrl = (root, product) => {
    const productHandle = product?.handle || '';
    const fallbackUrl = product?.url || (productHandle ? `/products/${encodeURIComponent(productHandle)}` : '#');
    const collectionHandle = getActiveCollectionHandle(root);

    if (!collectionHandle || !productHandle) return fallbackUrl;

    return `/collections/${encodeURIComponent(collectionHandle)}/products/${encodeURIComponent(productHandle)}`;
  };

  const getImage = (product) => product?.featuredImage || product?.image || product?.images?.[0] || null;

  const getPriceMarkup = (root, product) => {
    const currencyCode = product?.priceRange?.minVariantPrice?.currencyCode
      || product?.price?.currencyCode
      || root.dataset.shopTheCategoryCurrencyCode
      || 'USD';
    const price = moneyAmount(product?.priceRange?.minVariantPrice ?? product?.price);
    const maxPrice = moneyAmount(product?.priceRange?.maxVariantPrice ?? price);
    const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice);
    const productId = product?.numericId || product?.id || '';
    const priceVariant = (product?.variants || []).find((variant) => moneyAmount(variant?.price) === price);
    const variantId = String(priceVariant?.id || priceVariant?.numericId || product?.selectedVariantId || '').match(/(\d+)$/)?.[1] || '';
    const onSale = compareAtPrice > price;
    const showFrom = maxPrice > price;

    if (!price) return '';

    return `
      <span
        class="price${onSale ? ' price--on-sale' : ''} shop-the-category-product-card__price"
        data-product-price
        data-product-id="${escapeHtml(productId)}"
        data-variant-id="${escapeHtml(variantId)}"
        data-base-price-cents="${Math.round(price * 100)}"
        data-compare-at-price-cents="${Math.round((onSale ? compareAtPrice : 0) * 100)}"
        data-price-show-from="${showFrom ? 'true' : 'false'}"
      >
        ${onSale ? `<span class="visually-hidden" data-price-compare-label>Regular price</span><s class="price__compare shop-the-category-product-card__compare" data-price-compare>${formatMoney(compareAtPrice, currencyCode)}</s>` : ''}
        <span class="visually-hidden" data-price-current-label>${onSale ? 'Sale price' : 'Regular price'}</span>
        <span class="price__current" data-price-current>${showFrom ? 'From ' : ''}${formatMoney(price, currencyCode)}</span>
      </span>
    `;
  };

  const getShopTheCategoryTabData = (root) => {
    const script = root.querySelector('[data-shop-the-category-tabs-json]');
    if (!script?.textContent?.trim()) return [];

    try {
      const tabs = JSON.parse(script.textContent);

      return Array.isArray(tabs) ? tabs.filter((tab) => tab?.collectionHandle) : [];
    } catch (error) {
      return [];
    }
  };

  const getFallbackTab = (root) => ({
    id: root.dataset.shopTheCategoryActiveTab || 'section',
    label: root.querySelector('[data-shop-the-category-heading]')?.textContent?.trim() || 'Shop the category',
    collectionId: root.dataset.shopTheCategoryCollectionId || '',
    collectionHandle: root.dataset.shopTheCategoryCollectionHandle || '',
    collectionTitle: root.dataset.shopTheCategoryCollectionTitle || ''
  });

  const setTextContent = (element, value) => {
    if (!element) return;

    const text = String(value || '').trim();
    element.textContent = text;
    element.hidden = !text;
  };

  const setLinkContent = (link, label, href) => {
    if (!link) return;

    const text = String(label || '').trim();
    link.textContent = text;
    link.hidden = !text;

    if (href) {
      link.setAttribute('href', href);
    }
  };

  const updateShopTheCategoryContent = (root, tab) => {
    setTextContent(root.querySelector('[data-shop-the-category-eyebrow]'), tab.eyebrow);
    setTextContent(root.querySelector('[data-shop-the-category-heading]'), tab.heading);
    setTextContent(root.querySelector('[data-shop-the-category-text]'), tab.text);
    setLinkContent(root.querySelector('[data-shop-the-category-primary]'), tab.primaryButtonLabel, tab.primaryButtonLink);
    setLinkContent(root.querySelector('[data-shop-the-category-secondary]'), tab.secondaryButtonLabel, tab.secondaryButtonLink);
  };

  const clearShopTheCategoryTransition = (root) => {
    const timers = shopTheCategoryTransitionTimers.get(root) || [];

    timers.forEach((timer) => window.clearTimeout(timer));
    shopTheCategoryTransitionTimers.delete(root);
    root.classList.remove('shop-the-category--swap-out', 'shop-the-category--swap-in');
  };

  const runShopTheCategoryTransition = (root, requestToken, update) => {
    clearShopTheCategoryTransition(root);

    if (reducedMotionQuery?.matches) {
      update();
      return;
    }

    root.classList.add('shop-the-category--swap-out');

    const outTimer = window.setTimeout(() => {
      if (shopTheCategoryRequests.get(root) !== requestToken) return;

      update();
      root.classList.remove('shop-the-category--swap-out');
      root.classList.add('shop-the-category--swap-in');

      const inTimer = window.setTimeout(() => {
        root.classList.remove('shop-the-category--swap-in');
        shopTheCategoryTransitionTimers.delete(root);
      }, tabSwapInDuration);

      shopTheCategoryTransitionTimers.set(root, [inTimer]);
    }, tabSwapOutDuration);

    shopTheCategoryTransitionTimers.set(root, [outTimer]);
  };

  const updateTabState = (root, activeTab, { scrollActiveTab = true } = {}) => {
    let activeButton = null;

    root.querySelectorAll('[data-shop-the-category-tab]').forEach((button) => {
      const isActive = button.dataset.shopTheCategoryTabId === activeTab.id;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;

      if (isActive) {
        activeButton = button;
      }
    });

    if (scrollActiveTab && activeButton) {
      const tabList = activeButton.closest('.shop-the-category__tabs');

      if (tabList && tabList.scrollWidth > tabList.clientWidth + 1) {
        const tabListBounds = tabList.getBoundingClientRect();
        const activeButtonBounds = activeButton.getBoundingClientRect();
        const centeredScrollLeft = tabList.scrollLeft
          + activeButtonBounds.left
          - tabListBounds.left
          + ((activeButtonBounds.width - tabList.clientWidth) / 2);
        const maxScrollLeft = tabList.scrollWidth - tabList.clientWidth;

        tabList.scrollTo({
          left: Math.min(maxScrollLeft, Math.max(0, centeredScrollLeft)),
          behavior: reducedMotionQuery?.matches ? 'auto' : 'smooth'
        });
      }
    }
  };

  const updateRootDataset = (root, tab) => {
    root.dataset.shopTheCategoryActiveTab = tab.id || '';
    root.dataset.shopTheCategoryCollectionId = tab.collectionId || '';
    root.dataset.shopTheCategoryCollectionHandle = tab.collectionHandle || '';
    root.dataset.shopTheCategoryCollectionTitle = tab.collectionTitle || tab.label || '';
  };

  const isMobileShopTheCategory = () => mobileShopTheCategoryQuery?.matches ?? window.innerWidth < 750;

  const getPixelValue = (value) => {
    const pixels = Number.parseFloat(value);

    return Number.isFinite(pixels) ? pixels : 0;
  };

  const createCopyMeasure = () => {
    const measure = document.createElement('div');

    measure.className = 'shop-the-category__copy';
    measure.setAttribute('aria-hidden', 'true');
    measure.innerHTML = `
      <p class="shop-the-category__eyebrow" data-shop-the-category-measure-eyebrow></p>
      <h1 class="shop-the-category__heading" data-shop-the-category-measure-heading></h1>
      <div class="shop-the-category__text rte" data-shop-the-category-measure-text></div>
      <div class="shop-the-category__actions">
        <a class="button" href="#" data-shop-the-category-measure-primary></a>
        <a class="button button--secondary" href="#" data-shop-the-category-measure-secondary></a>
      </div>
    `;
    Object.assign(measure.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      zIndex: '-1',
      visibility: 'hidden',
      pointerEvents: 'none',
      opacity: '1',
      transform: 'none',
      transition: 'none'
    });

    return measure;
  };

  const setMeasureText = (element, value) => {
    if (!element) return;

    const text = String(value || '').trim();
    element.textContent = text;
    element.hidden = !text;
  };

  const setMeasureLink = (link, label, href) => {
    if (!link) return;

    const text = String(label || '').trim();
    link.textContent = text;
    link.hidden = !text;

    if (href) {
      link.setAttribute('href', href);
    }
  };

  const updateCopyMeasure = (measure, tab) => {
    setMeasureText(measure.querySelector('[data-shop-the-category-measure-eyebrow]'), tab.eyebrow);
    setMeasureText(measure.querySelector('[data-shop-the-category-measure-heading]'), tab.heading);
    setMeasureText(measure.querySelector('[data-shop-the-category-measure-text]'), tab.text);
    setMeasureLink(measure.querySelector('[data-shop-the-category-measure-primary]'), tab.primaryButtonLabel, tab.primaryButtonLink);
    setMeasureLink(measure.querySelector('[data-shop-the-category-measure-secondary]'), tab.secondaryButtonLabel, tab.secondaryButtonLink);
  };

  const getLargestCopyHeight = (root, panel, copy) => {
    const tabs = shopTheCategoryTabs.get(root) || [];
    const panelStyles = window.getComputedStyle(panel);
    const panelInnerWidth = panel.clientWidth - getPixelValue(panelStyles.paddingLeft) - getPixelValue(panelStyles.paddingRight);
    const copyWidth = Math.ceil(copy.getBoundingClientRect().width || panelInnerWidth);
    let largestHeight = Math.ceil(copy.getBoundingClientRect().height);

    if (!tabs.length || !copyWidth) return largestHeight;

    const measure = createCopyMeasure();

    measure.style.width = `${copyWidth}px`;
    measure.style.maxWidth = `${copyWidth}px`;
    panel.append(measure);

    tabs.forEach((tab) => {
      updateCopyMeasure(measure, tab);
      largestHeight = Math.max(largestHeight, Math.ceil(measure.getBoundingClientRect().height));
    });

    measure.remove();

    return largestHeight;
  };

  const syncPanelHeight = (root, { reset = false } = {}) => {
    if (reset) {
      root.style.removeProperty('--shop-the-category-panel-min-height');
    }

    const panel = root.querySelector('[data-shop-the-category-panel]');
    const copy = panel?.querySelector('.shop-the-category__copy');
    const slot = panel?.querySelector('[data-shop-the-category-product-card]');

    if (!panel || !copy) return;

    const panelStyles = window.getComputedStyle(panel);
    const copyHeight = getLargestCopyHeight(root, panel, copy);
    const feature = slot?.closest('.shop-the-category__feature');
    const hasProductSlot = slot && feature && window.getComputedStyle(feature).display !== 'none';
    const slotHeight = hasProductSlot ? Math.ceil(slot.getBoundingClientRect().height) : 0;
    const rowGap = slotHeight > 0 ? getPixelValue(panelStyles.rowGap || panelStyles.gap) : 0;
    const contentHeight = isMobileShopTheCategory()
      ? copyHeight + slotHeight + rowGap
      : Math.max(copyHeight, slotHeight);
    const panelHeight = Math.ceil(
      contentHeight
      + getPixelValue(panelStyles.paddingTop)
      + getPixelValue(panelStyles.paddingBottom)
    );
    const lockedHeight = getPixelValue(root.style.getPropertyValue('--shop-the-category-panel-min-height'));

    root.style.setProperty('--shop-the-category-panel-min-height', `${Math.max(lockedHeight, panelHeight)}px`);
  };

  const requestPanelHeightSync = (root, options = {}) => {
    const currentFrame = shopTheCategoryPanelHeightFrames.get(root);

    if (currentFrame) {
      window.cancelAnimationFrame(currentFrame);
    }

    const frame = window.requestAnimationFrame(() => {
      shopTheCategoryPanelHeightFrames.delete(root);
      syncPanelHeight(root, options);
    });

    shopTheCategoryPanelHeightFrames.set(root, frame);
  };

  const bindPanelHeight = (root) => {
    if (shopTheCategoryPanelHeightCleanups.has(root)) return;

    const sync = () => requestPanelHeightSync(root, { reset: true });

    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);

    if (mobileShopTheCategoryQuery?.addEventListener) {
      mobileShopTheCategoryQuery.addEventListener('change', sync);
    } else if (mobileShopTheCategoryQuery?.addListener) {
      mobileShopTheCategoryQuery.addListener(sync);
    }

    document.fonts?.ready?.then(sync).catch(() => {});
    shopTheCategoryPanelHeightCleanups.set(root, () => {
      const currentFrame = shopTheCategoryPanelHeightFrames.get(root);

      if (currentFrame) {
        window.cancelAnimationFrame(currentFrame);
        shopTheCategoryPanelHeightFrames.delete(root);
      }

      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);

      if (mobileShopTheCategoryQuery?.removeEventListener) {
        mobileShopTheCategoryQuery.removeEventListener('change', sync);
      } else if (mobileShopTheCategoryQuery?.removeListener) {
        mobileShopTheCategoryQuery.removeListener(sync);
      }

      root.style.removeProperty('--shop-the-category-panel-min-height');
    });

    sync();
  };

  const getRequestKey = (root) => {
    const endpoint = normalizeEndpoint(root.dataset.shopTheCategoryWorkerEndpoint);
    const collectionId = root.dataset.shopTheCategoryCollectionId || '';
    const collectionHandle = root.dataset.shopTheCategoryCollectionHandle || '';
    const productLimit = Math.max(productCardCount, Math.min(Number(root.dataset.shopTheCategoryProductLimit) || productCardCount, 5));
    const sortKey = root.dataset.shopTheCategorySortKey || 'recommended';

    if (!endpoint || !collectionHandle || !('fetch' in window)) return '';

    const currencyCode = window.WobWorkerMarket?.activeCurrency
      || root.dataset.shopTheCategoryCurrencyCode
      || 'default';

    return [endpoint, collectionId, collectionHandle, productLimit, sortKey, currencyCode, 'shop-the-category'].join('|');
  };

  const requestProducts = (root) => {
    const cacheKey = getRequestKey(root);

    if (!cacheKey) return Promise.resolve([]);

    const cachedProducts = readCachedProducts(cacheKey);

    if (cachedProducts?.length >= productCardCount) {
      return Promise.resolve(cachedProducts);
    }

    if (productRequests.has(cacheKey)) return productRequests.get(cacheKey);

    const [endpoint, collectionId, collectionHandle, productLimit, sortKey] = cacheKey.split('|');
    const request = fetch(`${endpoint}/collections/products`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        collectionId,
        collectionHandle,
        pageSize: Number(productLimit) || 5,
        sortKey,
        filters: [],
        source: 'shop-the-category'
      })
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);

        if (!response.ok || !payload?.ok || payload?.fallback?.recommended) {
          throw new Error(payload?.fallback?.reason || 'shop-the-category-worker-unavailable');
        }

        const products = Array.isArray(payload.products) ? payload.products : [];
        const localizedProducts = await (window.WobWorkerMarket?.localizeProducts?.(products) || Promise.resolve(products));

        if (localizedProducts.length >= productCardCount) {
          writeCachedProducts(cacheKey, localizedProducts);
        }

        return localizedProducts;
      });

    productRequests.set(cacheKey, request);
    request.catch(() => productRequests.delete(cacheKey));

    return request;
  };

  const renderSkeleton = (root, slot) => {
    root.classList.remove('shop-the-category--product-ready', 'shop-the-category--product-unavailable');
    slot.setAttribute('aria-busy', 'true');
    cleanupProductRail(slot);
    slot.innerHTML = skeletonMarkup;
    bindProductRail(slot);
    requestPanelHeightSync(root);
  };

  const renderProductCard = (root, product, index) => {
    const image = getImage(product);
    const imageUrl = typeof image === 'string' ? image : image?.url || image?.src || '';
    const title = product?.title || 'Product';
    const productUrl = getProductUrl(root, product);
    const productId = product?.numericId || product?.id || '';
    const productHandle = product?.handle || '';

    return `
      <a
        class="shop-the-category-product-card"
        href="${escapeHtml(productUrl)}"
        data-shop-the-category-product-card
        data-product-id="${escapeHtml(productId)}"
        data-product-handle="${escapeHtml(productHandle)}"
        data-product-title="${escapeHtml(title)}"
        data-shop-the-category-result-position="${index + 1}"
      >
        <span class="shop-the-category-product-card__media">
          ${imageUrl ? `
            <img
              src="${escapeHtml(withImageWidth(imageUrl, 540))}"
              srcset="${escapeHtml(buildSrcset(imageUrl))}"
              sizes="(min-width: 990px) 210px, (min-width: 750px) 150px, 45vw"
              width="${typeof image === 'string' ? 540 : image?.width || 540}"
              height="${typeof image === 'string' ? 810 : image?.height || 810}"
              alt="${escapeHtml(image?.altText || image?.alt || title)}"
              loading="${index === 0 ? 'eager' : 'lazy'}"
              ${index === 0 ? 'fetchpriority="high"' : ''}
              decoding="async"
            >
          ` : ''}
        </span>
        <span class="shop-the-category-product-card__content">
          <span class="shop-the-category-product-card__title">${escapeHtml(title)}</span>
          <span class="shop-the-category-product-card__price-row">${getPriceMarkup(root, product)}</span>
        </span>
      </a>
    `;
  };

  const cleanupProductRail = (slot) => {
    const cleanup = productRailCleanups.get(slot);

    if (cleanup) {
      cleanup();
      productRailCleanups.delete(slot);
    }

    slot.classList.remove(
      'shop-the-category__product-slot--entering',
      'shop-the-category__product-slot--scrollable',
      'shop-the-category__product-slot--at-start',
      'shop-the-category__product-slot--at-end'
    );
  };

  const updateProductRailState = (slot, rail) => {
    const maxScroll = rail.scrollWidth - rail.clientWidth;
    const isScrollable = maxScroll > 1;

    slot.classList.toggle('shop-the-category__product-slot--scrollable', isScrollable);
    slot.classList.toggle('shop-the-category__product-slot--at-start', !isScrollable || rail.scrollLeft <= 1);
    slot.classList.toggle('shop-the-category__product-slot--at-end', !isScrollable || maxScroll - rail.scrollLeft <= 1);
  };

  const bindProductRail = (slot) => {
    const rail = slot.querySelector('.shop-the-category-products');

    if (!rail) return;

    const update = () => updateProductRailState(slot, rail);
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(update) : null;

    rail.addEventListener('scroll', update, { passive: true });
    resizeObserver?.observe(rail);
    window.addEventListener('resize', update);
    productRailCleanups.set(slot, () => {
      rail.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    });

    window.requestAnimationFrame(update);
  };

  const renderProducts = (root, slot, products) => {
    const visibleProducts = products.slice(0, productCardCount).filter(Boolean);

    if (visibleProducts.length < productCardCount) {
      throw new Error('shop-the-category-not-enough-products');
    }

    cleanupProductRail(slot);
    slot.innerHTML = `
      <div class="shop-the-category-products">
        ${visibleProducts.map((product, index) => renderProductCard(root, product, index)).join('')}
      </div>
    `;
    bindProductRail(slot);

    if (!reducedMotionQuery?.matches) {
      slot.classList.add('shop-the-category__product-slot--entering');
      window.setTimeout(() => {
        slot.classList.remove('shop-the-category__product-slot--entering');
      }, tabSwapInDuration);
    }

    root.classList.add('shop-the-category--product-ready');
    slot.removeAttribute('aria-busy');
    requestPanelHeightSync(root);
  };

  const selectTab = (root, tab, { updateContent = true, scrollActiveTab = true, animate = true } = {}) => {
    const slot = root.querySelector('[data-shop-the-category-product-card]');
    const requestToken = Symbol(tab.id || tab.collectionHandle || 'shop-the-category-tab');

    if (!slot || !tab?.collectionHandle) return;

    updateTabState(root, tab, { scrollActiveTab });
    shopTheCategoryRequests.set(root, requestToken);
    syncPanelHeight(root);

    const updateTab = () => {
      updateRootDataset(root, tab);

      if (updateContent) {
        updateShopTheCategoryContent(root, tab);
      }

      renderSkeleton(root, slot);

      requestProducts(root)
        .then((products) => {
          if (shopTheCategoryRequests.get(root) !== requestToken) return;

          renderProducts(root, slot, products);
        })
        .catch(() => {
          if (shopTheCategoryRequests.get(root) !== requestToken) return;

          slot.removeAttribute('aria-busy');
          cleanupProductRail(slot);
          slot.innerHTML = '';
          root.classList.add('shop-the-category--product-unavailable');
          requestPanelHeightSync(root);
        });
    };

    if (animate) {
      runShopTheCategoryTransition(root, requestToken, updateTab);
    } else {
      clearShopTheCategoryTransition(root);
      updateTab();
    }
  };

  const setTabProgress = (root, value) => {
    const progress = root.querySelector('[data-shop-the-category-tab-progress]');

    if (!progress) return;

    progress.style.transform = `scaleX(${Math.max(0, Math.min(value, 1))})`;
  };

  const stopTabAutoplay = (root, { reset = true } = {}) => {
    const state = shopTheCategoryTabAutoplay.get(root);

    if (state?.frameId) {
      window.cancelAnimationFrame(state.frameId);
    }

    shopTheCategoryTabAutoplay.delete(root);

    if (reset) {
      setTabProgress(root, 0);
    }
  };

  const getActiveTabIndex = (root, tabs) => {
    const activeTabId = root.dataset.shopTheCategoryActiveTab || '';
    const activeIndex = tabs.findIndex((tab) => tab.id === activeTabId);

    return activeIndex >= 0 ? activeIndex : 0;
  };

  const startTabAutoplay = (root) => {
    const tabs = shopTheCategoryTabs.get(root) || [];

    stopTabAutoplay(root);

    if (tabs.length < 2 || reducedMotionQuery?.matches || !root.querySelector('[data-shop-the-category-tab-progress]')) {
      return;
    }

    const state = {
      frameId: 0,
      startedAt: window.performance?.now?.() || Date.now()
    };

    const tick = (timestamp) => {
      const elapsed = timestamp - state.startedAt;
      const progress = elapsed / tabAutoplayDuration;

      setTabProgress(root, progress);

      if (progress >= 1) {
        const currentIndex = getActiveTabIndex(root, tabs);
        const nextIndex = (currentIndex + 1) % tabs.length;

        stopTabAutoplay(root);
        selectTab(root, tabs[nextIndex], { scrollActiveTab: true });
        startTabAutoplay(root);
        return;
      }

      state.frameId = window.requestAnimationFrame(tick);
    };

    shopTheCategoryTabAutoplay.set(root, state);
    state.frameId = window.requestAnimationFrame(tick);
  };

  const bindTabs = (root) => {
    const tabs = shopTheCategoryTabs.get(root) || [];
    const buttons = [...root.querySelectorAll('[data-shop-the-category-tab]')];

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const tab = tabs[Number(button.dataset.shopTheCategoryTabIndex) || 0];

        stopTabAutoplay(root);
        selectTab(root, tab);
      });

      button.addEventListener('keydown', (event) => {
        const currentIndex = buttons.indexOf(button);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = buttons.length - 1;
        else return;

        event.preventDefault();
        buttons[nextIndex]?.focus();
        stopTabAutoplay(root);
        selectTab(root, tabs[nextIndex]);
      });
    });
  };

  const initShopTheCategory = (root) => {
    if (initializedShopTheCategories.has(root)) return;

    const tabs = getShopTheCategoryTabData(root);
    const fallbackTab = getFallbackTab(root);
    const initialTab = tabs[0] || fallbackTab;

    if (!initialTab.collectionHandle) return;

    initializedShopTheCategories.add(root);
    shopTheCategoryTabs.set(root, tabs.length ? tabs : [fallbackTab]);
    bindPanelHeight(root);
    bindTabs(root);
    selectTab(root, initialTab, { updateContent: tabs.length > 0, scrollActiveTab: false, animate: false });
    startTabAutoplay(root);
  };

  const destroyShopTheCategory = (root) => {
    const cleanupPanelHeight = shopTheCategoryPanelHeightCleanups.get(root);
    const slot = root.querySelector('[data-shop-the-category-product-card]');

    stopTabAutoplay(root);
    clearShopTheCategoryTransition(root);

    if (slot) {
      cleanupProductRail(slot);
    }

    cleanupPanelHeight?.();
    shopTheCategoryPanelHeightCleanups.delete(root);
    shopTheCategoryRequests.delete(root);
    shopTheCategoryTabs.delete(root);
    initializedShopTheCategories.delete(root);
  };

  const init = (container = document) => {
    if (container.nodeType === 1 && container.matches?.('[data-shop-the-category]')) {
      initShopTheCategory(container);
    }

    container.querySelectorAll?.('[data-shop-the-category]').forEach(initShopTheCategory);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-shop-the-category]')) {
      destroyShopTheCategory(event.target);
    }

    event.target.querySelectorAll?.('[data-shop-the-category]').forEach(destroyShopTheCategory);
  });
})();
